import * as Yup from "yup";
import { formAdFields } from "../constants/data";

const normalizeEmptyToNull = (curr, orig) => {
  if (orig === undefined || orig === null) return null;
  if (typeof orig !== "string") return curr;
  return orig.trim() === "" ? null : curr;
};

const isHttpUrl = (v) => /^https?:\/\/.+/i.test(String(v).trim());

const stripDataUrlPrefixIfPresent = (v) => {
  if (!v) return v;
  const s = String(v).trim();
  const idx = s.indexOf("base64,");
  if (s.startsWith("data:") && idx !== -1)
    return s.substring(idx + "base64,".length);
  return s;
};

const isLikelyBase64 = (v) => {
  if (!v) return true;

  const raw = stripDataUrlPrefixIfPresent(v);
  if (!raw) return true;

  const s = String(raw).trim();

  // legacy: allow full URL
  if (isHttpUrl(s)) return true;

  // base64 charset only
  if (!/^[A-Za-z0-9+/=]+$/.test(s)) return false;

  // guard against absurd payloads
  if (s.length > 14_000_000) return false;

  return true;
};

export const schema = Yup.object().shape({
  // An empty picker submits "", which Yup reports as a type error, so both say the same.
  [formAdFields.teamId.short]: Yup.number()
    .typeError("Pick a team")
    .integer("Pick a team")
    .min(1, "Pick a team")
    .required("Pick a team"),

  [formAdFields.adType.short]: Yup.string()
    .oneOf(["CAMP", "CLINIC", "JOB", "SERVICE", "RETAIL", "MEET"])
    .required("Ad type is required"),

  [formAdFields.name.short]: Yup.string()
    .required("Enter ad name, please")
    .transform((curr, orig) =>
      typeof orig === "string" && orig.trim() === "" ? null : curr,
    )
    .min(1, "Must be min 1 symbols")
    .max(255, "Must be max 255 symbols")
    .nullable(),

  [formAdFields.clickUrl.short]: Yup.string()
    .transform(normalizeEmptyToNull)
    .nullable()
    .required("Click URL is required")
    .test(
      "is-valid-url",
      "Click URL must be a valid URL (include https://)",
      (v) => {
        // Yup required() will catch null/empty after transform, so here just validate format.
        if (!v) return true;
        return isHttpUrl(v);
      },
    ),

  // ✅ allow empty OR url OR base64
  [formAdFields.imageUrl.short]: Yup.string()
    .transform(normalizeEmptyToNull)
    .nullable()
    .test(
      "is-url-or-base64",
      "Image must be a valid URL or base64 payload",
      (v) => isLikelyBase64(v),
    ),

  [formAdFields.imageObjectKey.short]: Yup.string()
    .transform((curr, orig) => (orig?.trim?.() === "" ? null : curr))
    .nullable(),

  [formAdFields.status.short]: Yup.string()
    .oneOf(["DRAFT", "ACTIVE", "PAUSED", "ENDED"])
    .required(),

  [formAdFields.ages.short]: Yup.string().nullable(),
  [formAdFields.levels.short]: Yup.string().nullable(),

  [formAdFields.shortDescription.short]: Yup.string()
    .max(1000, "Must be max 1000 characters")
    .nullable(),

  [formAdFields.ctaText.short]: Yup.string()
    .transform(normalizeEmptyToNull)
    .nullable()
    .required("CTA text is required")
    .max(40, "Must be max 40 characters"),

  [formAdFields.city.short]: Yup.string()
    .max(120, "Must be max 120 characters")
    .nullable(),
  [formAdFields.region.short]: Yup.string()
    .max(120, "Must be max 120 characters")
    .nullable(),
  [formAdFields.country.short]: Yup.string()
    .max(2, "Use ISO-2 (e.g., US)")
    .nullable(),

  [formAdFields.maxUniqueImpressions.short]: Yup.number()
    .typeError("Max unique impressions must be a number")
    .integer("Must be an integer")
    .min(0, "Must be >= 0")
    .nullable(),

  [formAdFields.maxUniqueClicks.short]: Yup.number()
    .typeError("Max unique clicks must be a number")
    .integer("Must be an integer")
    .min(0, "Must be >= 0")
    .nullable(),

  [formAdFields.priority.short]: Yup.number()
    .typeError("Priority must be a number")
    .integer("Must be an integer")
    .min(0, "Must be >= 0")
    .nullable(),

  [formAdFields.startDate.short]: Yup.date().nullable(),
  [formAdFields.endDate.short]: Yup.date()
    .nullable()
    .when(formAdFields.startDate.short, (startDate, sch) => {
      if (!startDate) return sch;
      return sch.min(startDate, "End date must be on/after start date");
    }),

  [formAdFields.governingBodies.short]: Yup.array().of(
    Yup.object().shape({
      id: Yup.mixed().nullable(),
      abbreviation: Yup.string().nullable(),
      name: Yup.string().nullable(),
    }),
  ),
});
